'use client';

import { createContext, useContext, useMemo, useRef } from 'react';

import { logException } from '../../utils/log';

import type { SsrData, SsrSerializedData } from '../ssr/ssr-data';
import type { SsrDataBuilder } from '../ssr/ssr-data-builder';
import type { PropsWithChildren } from 'react';

const SsrDataContext = createContext<SsrData | null>(null);

interface Props extends PropsWithChildren {
  builder: SsrDataBuilder;
  ssrSerializedData: SsrSerializedData;
}

export function SsrDataProvider({ builder, ssrSerializedData, children }: Props) {
  const builderRef = useRef(builder);

  const ssrData = useMemo((): SsrData => {
    try {
      // Must copy data because the source data gets modified when it is serialized.
      // This is due to a strange behavior in the class-transformers library.
      const copiedData = JSON.parse(JSON.stringify(ssrSerializedData));
      builderRef.current.deserializeData(copiedData);
      return builderRef.current.ssrData;
    } catch (e) {
      console.error('Error deserializing SSR data');
      console.error(ssrSerializedData);
      logException('Error unserializing SSR Data', e);
      return {};
    }
  }, [ssrSerializedData]);

  return <SsrDataContext.Provider value={ssrData}>{children}</SsrDataContext.Provider>;
}

export function useSsrData<TData>(key: string): TData {
  const ssrData = useContext(SsrDataContext);
  if (!ssrData) {
    throw new Error('SSR data not found');
  }
  if (!(key in ssrData)) {
    throw new Error(`SSR data for key "${key}" not found`);
  }
  return ssrData[key] as TData;
}

export function useSsrDataOrNull<TData>(
  key: string,
  options?: { ignoreNotFoundError?: boolean }
): TData | null {
  const ssrData = useContext(SsrDataContext);
  if (!ssrData) {
    if (options?.ignoreNotFoundError) return null;
    throw new Error(`SSR Data not found for key "${key}"`);
  }
  if (!(key in ssrData)) {
    return null;
  }
  return ssrData[key] as TData;
}
